import { IDataSourceConfig } from "@multiweb/reactCore/datasource";
import { IAttrValue, IRowData } from "@multiweb/types/contracts";
import { ConditionParams, IDataSource, IDataSourceExtended, IDataSourceLoadInfo, IDataSourceLoadInfoBase, IOrderBy, IRecordsInfo } from "../../../Global/Base/DataSources/Interfaces";
import { CaseInsensitiveMap } from "../../../Global/Base/DataTypes/CaseInsensitiveMap";
import { Event } from "../../../Global/Base/Events";
import { InvalidArgumentException, NotImplementedException } from "../../../Global/Base/Exceptions";
import { CancellationToken } from "../../../Global/Base/Promises/CancellationTokenSource";
/**
 * Trida reprezentujici databazovy zdroj dat pro konzumenta dat
 */
export class AggregatedDataSource<T extends IDataSourceExtended> implements IDataSource {
    protected _dataSources: T[] = [];
    protected _dataSourcesByClassName: CaseInsensitiveMap<{ ds: T; convertor: (data: IRowData) => IRowData }> = new CaseInsensitiveMap<{ ds: T; convertor: (data: IRowData) => IRowData }>();
    protected _attrs: string[];
    protected _fullLengthAttributes: string[] = [];

    /**
    * Vraci radek a zmeneny atribut
    */
    public readonly attributesChanged: Event<IDataSource, { key: any; isChanged: boolean; values: Map<string, IAttrValue> }> = new Event<IDataSource, { key: any; isChanged: boolean; values: Map<string, IAttrValue> }>();

    /**
     * Vraci startindex pro cteni dat
     */
    public readonly dataLoaded: Event<IDataSource, IDataSourceLoadInfo> = new Event<IDataSource, IDataSourceLoadInfo>();

    /**
     * Vyvolano po docteni dat ze vsech datasourcu
     */
    public readonly dataLoadedAll: Event<IDataSource, CaseInsensitiveMap<IDataSourceLoadInfo>> = new Event<IDataSource, CaseInsensitiveMap<IDataSourceLoadInfo>>();

    /**
     * Pokud je vracena pri cteni dat chyba je vyvolana tato udalost
     */
    public readonly error: Event<IDataSource, any> = new Event<IDataSource, any>();

    /**
     * Pokud je spuštěno načítání dat
     */
    public readonly loadingStarted: Event<IDataSource, Promise<boolean>> = new Event<IDataSource, Promise<boolean>>();

    /**
     * Pokud je ukončeno načítání dat
     */
    public readonly loadingEnded: Event<IDataSource, IDataSourceLoadInfoBase> = new Event<IDataSource, IDataSourceLoadInfoBase>();

    /**
     * Pokud je zrušeno načítání dat
     */
    public readonly loadingCancelled: Event<IDataSource, IDataSourceLoadInfoBase> = new Event<IDataSource, IDataSourceLoadInfoBase>();
    //#endregion Events

    //#region Fields
    /**
     * Nazev atributu pouziteho jako ID
     */
    public readonly dataKey: string = null;

    public readonly className: string = null;

    private _loadDataEventInfo: string[] = [];
    private _loadDataEventData: CaseInsensitiveMap<IDataSourceLoadInfo> = new CaseInsensitiveMap<IDataSourceLoadInfo>();

    public [Symbol.iterator](): Iterator<IRowData> {
        throw new Error("Method not implemented.");
    }

    /**
     * Property slouzci k definici trideni
     */
    public orderBy: IOrderBy[];
    //#endregion Fields

    //#region Properties
    /**
     * Vrací počet dataSourců
     */
    public get length(): number {
        return this._dataSources && this._dataSources.length || 0;
    }

    /**
     * Nastaví podmínku potřebnou pro načtení dat
     */
    public set condition(value: string) {
        for (const dataSource of this._dataSources) {
            dataSource.condition = value;
        }
    }

    /**
    * Vrací podmínku potřebnou pro načtení dat
    */
    public getOriginalCondition(): string {
        throw new NotImplementedException();
    }

    /***
     * Nastaví vstupy pro podmínku
     */
    public set conditionParams(value: ConditionParams) {
        for (const dataSource of this._dataSources) {
            dataSource.conditionParams = value;
        }
    }
    //#endregion Properties

    //#region Methods

    constructor(_config?: IDataSourceConfig) {
        // Prazdny konstruktor
    }

    /**
     * Vrací pole atributů, které datasource čte
     */
    public getAttrs(): string[] {
        return this._attrs;
    }

    /** Vrací pole atributů, jejichž hodnoty se nemají ořezávat */
    public getFullLengthAttrs(): string[] {
        return this._fullLengthAttributes;
    }

    /**
     * Nastavi atributy, ktere chci docist
     * @param attrs seznam atributu
        * @param fullLengthAttributes Seznam atributu, jejichž hodnoty se nemají ořezávat
*/
    public setAttrs(attrs: string[], fullLengthAttributes?: string[]): void {
        this._attrs = attrs;
        this._fullLengthAttributes = fullLengthAttributes || [];
    }

    /**
     * Nastavi atributy na datasource pro danou tridu
     * @param className Jméno třídy
     * @param attrs Seznam atributu
     * @param convert Konverzní funkce
     */
    public setAttrsByClassName(className: string, attrs: string[], convert?: (data: IRowData) => IRowData): void {
        const ds = this._dataSourcesByClassName.get(className);
        if (!ds)
            return;
        ds.ds.setAttrs(attrs);
        ds.convertor = convert;
    }

    /**
     * Nastaví atributy, ktere chci dočíst a dočte data, pokud přibyl nějaký nový atribut
     * @param _attrs seznam atributu
     */
    public setAttrsAndLoad(_attrs: string[]): Promise<any> {
        return Promise.resolve();
    }

    /**
     * Smaže dočtené záznamy v datasoursu
     * */
    public clearData(): void {
        for (const dataSource of this._dataSources) {
            dataSource.clearData();
        }
    }

    /**
     * Nahraje data od zacatku
     * @param keepSelectedRecords Příznak, zda mají po dočtení zůstat dříve označené záznamy
     * @param cancellationToken Token umožňující zrušení načítání na všech obsažených dataSourců
     */
    public loadNewData(keepSelectedRecords?: boolean, cancellationToken?: CancellationToken): Promise<any> {
        const prms = Array<Promise<any>>();

        if (cancellationToken)
            cancellationToken.register(() => {
                this._loadDataEventInfo = [];
                this._loadDataEventData.clear();
                this.loadingCancelled.trigger(this, { isFiltered: false, emptyData: true, cancellationToken: cancellationToken });
            });

        for (const ds of this._dataSources) {
            prms.push(ds.loadNewData(keepSelectedRecords, cancellationToken));
        }

        return Promise.all(prms);
    }

    /**
     * Vyvola dotaz na dalsi varku dat
     * @param keepSelectedRecords Příznak, zda mají po dočtení zůstat dříve označené záznamy
     */
    public getNextData(startIndex?: number, count?: number, keepSelectedRecords?: boolean): Promise<any> {
        const prms = Array<Promise<any>>();
        for (const ds of this._dataSources) {
            prms.push(ds.getNextData(startIndex, count, keepSelectedRecords));
        }

        return Promise.all(prms).then(() => { /* Prázdná arrow funkce */ });
    }

    /**
     * Metoda aktualizuje data datasource
     * @param fetchCount Volitelný počet záznamů, které se mají refreshnout, pokud není vyplněný, tak refresh řídí standartním fetchCountem
     */
    public refreshData(fetchCount?: number): Promise<any> {
        const prms = Array<Promise<any>>();
        for (const ds of this._dataSources) {
            prms.push(ds.refreshData(fetchCount));
        }

        return Promise.all(prms);
    }

    /**
     * Pokud zrovna probíhá načítání nebo reload dat, tak vrací promise, pomocí kterého jde na dočítání počkat, jinak vrací dokončený promise
     */
    public waitForReload(): Promise<void> {
        const prms = Array<Promise<any>>();
        for (const ds of this._dataSources) {
            prms.push(ds.waitForReload());
        }

        return Promise.all(prms).then(() => { /* Prázdná arrow funkce */ });
    }

    /**
     * Nastaví podmínku na základě třídy
     * @param className Název třídy
     * @param cond Podmínka
     */
    public setConditionByClassName(uniqeId: string, cond: string): void {
        const ds = this._dataSourcesByClassName.get(uniqeId);
        if (!ds)
            return;

        ds.ds.condition = cond;
    }

    /**
     * Nastaví dodatečnou podmínku na základě třídy
     * @param className Název třídy
     * @param condId identifikator podmínky
     * @param cond Podmínka
     */
    public setAdditionalConditionByClassName(uniqeId: string, condId: string, cond: string): void {
        const ds = this._dataSourcesByClassName.get(uniqeId);
        if (!ds)
            return;
        ds.ds.setAdditionalCondition(condId, cond);
    }

    /**
     * Nastaví parametry podmínky
     * @param className Název třídy
     * @param conditionParams Hodnoty parametrů podmínky
     */
    public setConditionParamsByClassName(uniqeId: string, conditionParams: ConditionParams): void {
        const ds = this._dataSourcesByClassName.get(uniqeId);
        if (!ds)
            return;

        ds.ds.conditionParams = conditionParams;
    }

    /**
     * Zaregistruje novy data source
     * @param ds instance datasource
     * @param className trida data source
     * @param uniqeId Nepovinný unikátní idenfitikátor pokud se registruje více stejných tříd slouží k jejich rozdělení
     * @param convert konverzni funkce, ktera provadi konverzi vracenych dat z ds na data agregovaneho datasource
     */
    public registerDataSource(ds: T, className: string, uniqeId: string | null = null, convert?: (data: IRowData) => IRowData): void {
        if (ds == null)
            throw new InvalidArgumentException("ds");
        if (className == null || className === "")
            throw new InvalidArgumentException("className");

        const classNameId = uniqeId == null ? className : uniqeId;
        if (this._dataSourcesByClassName.has(classNameId)) {
            return;
        }

        if (typeof convert !== "function") {
            convert = data => data;
        }

        this._dataSources.push(ds);
        this._dataSourcesByClassName.set(classNameId, { ds: ds, convertor: convert });

        ds.dataLoaded.add((_sender, prm) => {
            prm.additionalData = { uniqueId: uniqeId };

            if (this._loadDataEventInfo.includes(classNameId)) { // doslo k opakovanemu zavolani cele pole se vymaze
                this._loadDataEventInfo = [];
                this._loadDataEventData.clear();
            }
            const generator = prm.dataGenerator();

            //prm.additionalData.dataGenerator = function*() {
            prm.dataGenerator = function* () {
                for (const item of generator)
                    yield convert(item);
            };

            this._loadDataEventInfo.push(classNameId);
            this._loadDataEventData.set(classNameId, prm);

            this.dataLoaded.trigger(ds, prm);

            // pokud uz sou nacteny vsechny zdroje
            if (this._loadDataEventInfo.length == this._dataSources.length) {
                this.dataLoadedAll.trigger(this, this._loadDataEventData);

                this._loadDataEventInfo = [];
                this._loadDataEventData = new CaseInsensitiveMap<IDataSourceLoadInfo>();
            }
        });

        ds.error.add((dataSource, error) => {
            this.error.trigger(dataSource, error);
        },
        );
    }

    public getDataKey(uniqueId: string): string {
        const dsData = this._dataSourcesByClassName.get(uniqueId);
        return dsData ? dsData.ds.dataKey : "id";
    }

    // @ts-ignore // nevyužito, ale ponecháno
    private *_getConvertItemsIterator(convert: (data: IRowData) => IRowData, data: IterableIterator<IRowData>): IterableIterator<IRowData> {
        for (const item of data)
            yield convert(item);
    }

    // @ts-ignore // nevyužito, ale ponecháno
    private _each(callback: (item: IDataSource, resolveData: { cancelled: boolean }) => void): void {
        const rData = { cancelled: false };
        for (const ds of this._dataSources) {
            callback(ds, rData);
            if (rData.cancelled)
                break;
        }
    }

    /**
     * Vrací class name asociované s konkrétním datovým zdrojem.
     * @param uniqueId ID datového zdroje.
     */
    public getClassName(uniqueId: string): string {
        const dsData = this._dataSourcesByClassName.get(uniqueId);
        return dsData ? dsData.ds.className : undefined;
    }
    public setFilterCondition(_condition: string): void {
        throw new NotImplementedException();
    }
    public getFilterCondition(): string {
        throw new NotImplementedException();
    }
    /**
     * Vrací true, pokud je datasource filtrován.
     */
    public get hasFilterCondition(): boolean {
        return false;
    }
    /**
     * Metoda pro dočtení jednoho určitého záznamu
     * @param dataKey Datový klíč (většinou ID) k načtení
     * @param selectLoaded Příznak, zda se dočtené záznamy mají označit
     */
    public async loadSpecificRecord(_dataKey: string, _selectLoaded: boolean = false, _cancellationToken: CancellationToken = CancellationToken.none): Promise<IRowData> {
        throw new NotImplementedException();
    }

    /**
    * Metoda pro dočtení určitých záznamů
    * @param dataKeys Pole datových klíčů (většinou ID) k načtení
    * @param selectLoaded Příznak, zda se dočtené záznamy mají označit
    */
    public async loadSpecificRecords(_dataKeys: string[], _selectLoaded: boolean = false, _cancellationToken: CancellationToken = CancellationToken.none): Promise<IRowData[]> {
        throw new NotImplementedException();
    }

    /*
    * Vrací počet zadaných záznamů pro aktuálně nastavený filtr
    */
    public count(): Promise<IRecordsInfo> {
        throw new NotImplementedException();
    }
    //#endregion Methods
}
